import React, { useState, useEffect } from 'react';
import { useAuth } from '../../lib/auth/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { 
  Download, 
  Phone, 
  Mail, 
  MessageCircle, 
  MapPin, 
  Calendar, 
  Clock,
  TrendingUp,
  Users,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Eye,
  FileDown,
  Filter,
  Search,
  Trash2
} from 'lucide-react';
import { Authenticated } from '../../lib/auth/auth-components';
import { toast } from '../../lib/toast-utils';
import { Id } from '../../convex/_generated/dataModel';
import { useToast } from '../ui/use-toast';

// Enhanced Lead interface with download form specific fields
interface Lead {
  _id: Id<"leads">;
  _creationTime: number;
  name: string;
  email: string;
  phone: {
    countryCode: string;
    number: string;
  };
  projectId: Id<"projects">;
  language: string;
  purpose?: string;
  budget?: string;
  timeline?: string;
  bedrooms?: string;
  contactPreference?: string;
  formType?: string;
  utmId?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmContent?: string;
  utmTerm?: string;
  ipAddress?: string;
  userAgent?: string;
  visitorId?: string;
  status: string;
  createdAt: number;
  lastContactDate?: number;
  // Spam detection fields
  spamScore?: number;
  spamReasons?: string[];
  isSpam?: boolean;
  spamLevel?: string;
}

interface Project {
  _id: Id<"projects">;
  name: string;
  slug: string;
  status: string;
}

interface LeadStats {
  total: number;
  byStatus: Record<string, number>;
  byPurpose: Record<string, number>;
  bySource: Record<string, number>;
  recentLeads: Array<{
    _id: Id<"leads">;
    name: string;
    email: string;
    createdAt: number;
    status: string;
    isSpam?: boolean;
    spamScore?: number;
  }>;
}

interface SpamStats {
  total: number;
  validLeads: number;
  spamLeads: number;
  bySpamLevel: Record<string, number>;
  recentSpamAttempts: Array<{
    _id: Id<"leads">;
    name: string;
    email: string;
    spamScore: number;
    spamReasons: string[];
    createdAt: number;
  }>;
}

const LeadManagementContent: React.FC = () => {
  const { user, useAuthenticatedQuery, useAuthenticatedMutation } = useAuth();
  const [selectedProject, setSelectedProject] = useState<Id<"projects"> | null>(null);
  const [selectedTab, setSelectedTab] = useState<'all' | 'investment' | 'end-user' | 'spam'>('all');
  const [selectedFormType, setSelectedFormType] = useState<string>('all');
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [includeSpam, setIncludeSpam] = useState(false);
  const [activeTab, setActiveTab] = useState('all-leads');
  const { toast } = useToast();

  // Queries
  const projects = useAuthenticatedQuery('projects:list', {}) || [];
  const leads = useAuthenticatedQuery('leads:listByProject', 
    selectedProject !== null 
      ? { 
          projectId: selectedProject, 
          status: selectedStatus !== 'all' ? selectedStatus : undefined,
          includeSpam: includeSpam,
          formType: selectedFormType !== 'all' ? selectedFormType : undefined
        } 
      : "skip"
  ) || [];
  
  const leadStats = useAuthenticatedQuery('leads:getStats', 
    selectedProject !== null 
      ? { projectId: selectedProject }
      : {}
  ) as LeadStats | undefined;
  
  const spamStats = useAuthenticatedQuery('leads:getSpamStats', 
    selectedProject !== null 
      ? { projectId: selectedProject }
      : {}
  ) as SpamStats | undefined;

  // Mutations
  const updateLeadStatus = useAuthenticatedMutation('leads:updateStatus');
  const markAsNotSpam = useAuthenticatedMutation('leads:markAsNotSpam');
  const deleteLead = useAuthenticatedMutation('leads:deleteLead');
  const markAsSpam = useAuthenticatedMutation('leads:markAsSpam');

  // Filter leads based on search term and purpose
  const filterLeads = (leads: Lead[], purpose?: string, formType?: string) => {
    return leads.filter(lead => {
      const matchesSearch = !searchTerm || 
        lead.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        lead.email.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesPurpose = !purpose || lead.purpose === purpose;
      const matchesFormType = !formType || lead.formType === formType;
      
      return matchesSearch && matchesPurpose && matchesFormType;
    });
  };

  // Separate leads by form type
  const allLeads = filterLeads(leads);
  const downloadLeads = filterLeads(leads, undefined, 'download_form');
  const leadFormLeads = filterLeads(leads, undefined, 'lead_form');
  const spamLeads = leads.filter(lead => lead.isSpam);

  const handleStatusUpdate = async (leadId: Id<"leads">, newStatus: string) => {
    try {
      await updateLeadStatus({ leadId, status: newStatus });
      toast({
        title: 'Status Updated',
        description: 'Lead status has been updated successfully',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update lead status',
        variant: 'destructive'
      });
    }
  };

  const handleMarkAsNotSpam = async (leadId: Id<"leads">) => {
    try {
      await markAsNotSpam({ leadId });
      toast({
        title: 'Updated',
        description: 'Lead has been marked as not spam',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update spam status',
        variant: 'destructive'
      });
    }
  };

  const handleDeleteLead = async (leadId: Id<"leads">) => {
    try {
      await deleteLead({ leadId });
      toast({
        title: 'Deleted',
        description: 'Lead has been deleted successfully',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete lead',
        variant: 'destructive'
      });
    }
  };

  const handleMarkAsSpam = async (leadId: Id<"leads">) => {
    try {
      await markAsSpam({ leadId });
      toast({
        title: 'Updated',
        description: 'Lead has been marked as spam',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to mark lead as spam',
        variant: 'destructive'
      });
    }
  };

  const getStatusBadge = (status: string, isSpam?: boolean, spamScore?: number) => {
    if (isSpam) {
      const spamLevel = spamScore && spamScore > 70 ? 'high' : spamScore && spamScore > 40 ? 'moderate' : 'low';
      return (
        <Badge variant="destructive" className="gap-1">
          <AlertTriangle className="w-3 h-3" />
          Spam ({spamLevel})
        </Badge>
      );
    }

    const variants: Record<string, any> = {
      'new': { variant: 'default', icon: Clock },
      'contacted': { variant: 'secondary', icon: Phone },
      'qualified': { variant: 'outline', icon: CheckCircle },
      'converted': { variant: 'default', icon: TrendingUp },
      'closed': { variant: 'secondary', icon: XCircle }
    };

    const config = variants[status] || { variant: 'outline', icon: Eye };
    const Icon = config.icon;

    return (
      <Badge variant={config.variant} className="gap-1">
        <Icon className="w-3 h-3" />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatPhoneNumber = (phone: { countryCode: string; number: string }) => {
    return `${phone.countryCode} ${phone.number}`;
  };

  const getContactIcon = (preference?: string) => {
    switch (preference) {
      case 'whatsapp':
        return <MessageCircle className="w-4 h-4 text-green-600" />;
      case 'phone':
        return <Phone className="w-4 h-4 text-blue-600" />;
      case 'email':
        return <Mail className="w-4 h-4 text-gray-600" />;
      default:
        return <Mail className="w-4 h-4 text-gray-600" />;
    }
  };

  const exportLeads = (leadsToExport: Lead[], filename: string) => {
    const headers = [
      'Name', 'Email', 'Phone', 'Purpose', 'Budget', 'Timeline', 
      'Bedrooms', 'Contact Preference', 'UTM Source', 'Status', 
      'Created At', 'Is Spam', 'Spam Score'
    ];
    
    const csvContent = [
      headers.join(','),
      ...leadsToExport.map(lead => [
        lead.name,
        lead.email,
        formatPhoneNumber(lead.phone),
        lead.purpose || '',
        lead.budget || '',
        lead.timeline || '',
        lead.bedrooms || '',
        lead.contactPreference || '',
        lead.utmSource || '',
        lead.status,
        formatDate(lead.createdAt),
        lead.isSpam ? 'Yes' : 'No',
        lead.spamScore || ''
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const StatsCards = ({ title, stats }: { title: string; stats?: LeadStats | SpamStats }) => {
    if (!stats) return null;

    const isSpamStats = 'spamLeads' in stats;

    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {isSpamStats ? 'Total Submissions' : 'Total Leads'}
            </CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total}</div>
          </CardContent>
        </Card>

        {isSpamStats ? (
          <>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Valid Leads</CardTitle>
                <CheckCircle className="h-4 w-4 text-green-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">
                  {(stats as SpamStats).validLeads}
                </div>
                <p className="text-xs text-muted-foreground">
                  {stats.total > 0 ? Math.round(((stats as SpamStats).validLeads / stats.total) * 100) : 0}% of total
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Spam Filtered</CardTitle>
                <AlertTriangle className="h-4 w-4 text-red-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-red-600">
                  {(stats as SpamStats).spamLeads}
                </div>
                <p className="text-xs text-muted-foreground">
                  {stats.total > 0 ? Math.round(((stats as SpamStats).spamLeads / stats.total) * 100) : 0}% filtered out
                </p>
              </CardContent>
            </Card>
          </>
        ) : (
          <>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">New Leads</CardTitle>
                <Clock className="h-4 w-4 text-blue-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-blue-600">
                  {(stats as LeadStats).byStatus['new'] || 0}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Converted</CardTitle>
                <TrendingUp className="h-4 w-4 text-green-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">
                  {(stats as LeadStats).byStatus['converted'] || 0}
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    );
  };

  const LeadTable = ({ leads, title, showPurpose = false }: { 
    leads: Lead[]; 
    title: string; 
    showPurpose?: boolean; 
  }) => (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <div>
            <CardTitle className="flex items-center gap-2">
              {title === 'Download Form Leads' && <Download className="w-5 h-5" />}
              {title}
              <Badge variant="secondary">{leads.length}</Badge>
            </CardTitle>
            <CardDescription>
              {title === 'Download Form Leads' 
                ? 'Leads from brochure download forms'
                : 'Regular contact form submissions'
              }
            </CardDescription>
          </div>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => exportLeads(
              leads, 
              `${title.toLowerCase().replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.csv`
            )}
            className="gap-2"
          >
            <FileDown className="w-4 h-4" />
            Export
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {leads.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No leads found</p>
            <p className="text-sm">Leads will appear here once submitted</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-2">Contact</th>
                  {showPurpose && <th className="text-left p-2">Purpose</th>}
                  <th className="text-left p-2">Details</th>
                  <th className="text-left p-2">Source</th>
                  <th className="text-left p-2">Status</th>
                  <th className="text-left p-2">Date</th>
                  <th className="text-left p-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((lead) => (
                  <tr key={lead._id} className="border-b hover:bg-muted/50">
                    <td className="p-2">
                      <div className="space-y-1">
                        <div className="font-medium">{lead.name}</div>
                        <div className="text-muted-foreground text-xs flex items-center gap-1">
                          <Mail className="w-3 h-3" />
                          {lead.email}
                        </div>
                        <div className="text-muted-foreground text-xs flex items-center gap-1">
                          <Phone className="w-3 h-3" />
                          {formatPhoneNumber(lead.phone)}
                        </div>
                      </div>
                    </td>
                    
                    {showPurpose && (
                      <td className="p-2">
                        <Badge variant="outline">
                          {lead.purpose === 'download_brochure' ? 'Download' : lead.purpose || 'Contact'}
                        </Badge>
                      </td>
                    )}
                    
                    <td className="p-2">
                      <div className="space-y-1 text-xs">
                        {lead.budget && (
                          <div className="flex items-center gap-1">
                            <span className="text-muted-foreground">
                              {lead.formType === 'download_form' ? 'Previous Purchases:' : 'Budget:'}
                            </span>
                            <span>{lead.budget}</span>
                          </div>
                        )}
                        {lead.timeline && (
                          <div className="flex items-center gap-1">
                            <span className="text-muted-foreground">
                              {lead.formType === 'download_form' ? 'Investment Timeline:' : 'Timeline:'}
                            </span>
                            <span>{lead.timeline}</span>
                          </div>
                        )}
                        {lead.bedrooms && (
                          <div className="flex items-center gap-1">
                            <span className="text-muted-foreground">Bedrooms:</span>
                            <span>{lead.bedrooms}</span>
                          </div>
                        )}
                        {lead.contactPreference && (
                          <div className="flex items-center gap-1">
                            {getContactIcon(lead.contactPreference)}
                            <span>{lead.contactPreference}</span>
                          </div>
                        )}
                        {lead.formType && (
                          <div className="flex items-center gap-1">
                            <span className="text-muted-foreground">Form:</span>
                            <span className="capitalize">{lead.formType.replace('_', ' ')}</span>
                          </div>
                        )}
                      </div>
                    </td>
                    
                    <td className="p-2">
                      {lead.utmSource && (
                        <Badge variant="secondary" className="text-xs">
                          {lead.utmSource}
                        </Badge>
                      )}
                    </td>
                    
                    <td className="p-2">
                      {getStatusBadge(lead.status, lead.isSpam, lead.spamScore)}
                    </td>
                    
                    <td className="p-2 text-xs text-muted-foreground">
                      {formatDate(lead.createdAt)}
                    </td>
                    
                    <td className="p-2">
                      <div className="flex gap-1">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setSelectedLead(lead)}
                        >
                          <Eye className="w-3 h-3" />
                        </Button>
                        
                        {lead.isSpam ? (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleMarkAsNotSpam(lead._id)}
                            className="text-green-600"
                            title="Mark as not spam"
                          >
                            <CheckCircle className="w-3 h-3" />
                          </Button>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleMarkAsSpam(lead._id)}
                            className="text-yellow-600"
                            title="Mark as spam"
                          >
                            <AlertTriangle className="w-3 h-3" />
                          </Button>
                        )}
                        
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeleteLead(lead._id)}
                          className="text-red-600"
                          title="Delete lead"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );

  const LeadDetailModal = ({ lead, onClose }: { lead: Lead; onClose: () => void }) => (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-start mb-6">
          <div>
            <h2 className="text-xl font-bold">{lead.name}</h2>
            <p className="text-muted-foreground">{lead.email}</p>
          </div>
          <Button variant="ghost" onClick={onClose}>×</Button>
        </div>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-muted-foreground">Phone</label>
              <p>{formatPhoneNumber(lead.phone)}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Status</label>
              <div className="mt-1">
                <Select
                  value={lead.status}
                  onValueChange={(value) => handleStatusUpdate(lead._id, value)}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new">New</SelectItem>
                    <SelectItem value="contacted">Contacted</SelectItem>
                    <SelectItem value="qualified">Qualified</SelectItem>
                    <SelectItem value="converted">Converted</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {lead.purpose && (
            <div>
              <label className="text-sm font-medium text-muted-foreground">Purpose</label>
              <p className="capitalize">{lead.purpose.replace('_', ' ')}</p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            {lead.budget && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">
                  {lead.formType === 'download_form' ? 'Previous Purchases' : 'Budget'}
                </label>
                <p>{lead.budget}</p>
              </div>
            )}
            {lead.timeline && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">
                  {lead.formType === 'download_form' ? 'Investment Timeline' : 'Timeline'}
                </label>
                <p>{lead.timeline}</p>
              </div>
            )}
          </div>

          {lead.bedrooms && (
            <div>
              <label className="text-sm font-medium text-muted-foreground">Bedrooms</label>
              <p>{lead.bedrooms}</p>
            </div>
          )}

          {lead.formType && (
            <div>
              <label className="text-sm font-medium text-muted-foreground">Form Type</label>
              <p className="capitalize">{lead.formType.replace('_', ' ')}</p>
            </div>
          )}

          {lead.contactPreference && (
            <div>
              <label className="text-sm font-medium text-muted-foreground">Contact Preference</label>
              <p className="flex items-center gap-2">
                {getContactIcon(lead.contactPreference)}
                {lead.contactPreference}
              </p>
            </div>
          )}

          {(lead.utmSource || lead.utmMedium || lead.utmCampaign || lead.utmId) && (
            <div>
              <label className="text-sm font-medium text-muted-foreground">UTM Parameters</label>
              <div className="space-y-1 text-sm">
                {lead.utmId && <p>Campaign ID: <span className="font-medium">{lead.utmId}</span></p>}
                {lead.utmSource && <p>Source: <span className="font-medium">{lead.utmSource}</span></p>}
                {lead.utmMedium && <p>Medium: <span className="font-medium">{lead.utmMedium}</span></p>}
                {lead.utmCampaign && <p>Campaign: <span className="font-medium">{lead.utmCampaign}</span></p>}
                {lead.utmContent && <p>Content: <span className="font-medium">{lead.utmContent}</span></p>}
                {lead.utmTerm && <p>Term: <span className="font-medium">{lead.utmTerm}</span></p>}
              </div>
            </div>
          )}

          {lead.visitorId && (
            <div className="border border-blue-200 rounded-lg p-4 bg-blue-50">
              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium text-blue-800">Visitor Tracking</label>
                  <p className="text-sm text-blue-700 mt-1">
                    Visitor ID: <span className="font-mono text-xs">{lead.visitorId}</span>
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-blue-600 border-blue-200 hover:bg-blue-100"
                  onClick={() => {
                    // You can implement navigation to visitor analytics with this visitor ID
                    toast({
                      title: "Visitor Journey",
                      description: "Navigate to Analytics > Visitor Journey to see this visitor's complete journey.",
                    });
                  }}
                >
                  View Journey
                </Button>
              </div>
            </div>
          )}

          {lead.isSpam && (
            <div className="border border-red-200 rounded-lg p-4 bg-red-50">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-4 h-4 text-red-600" />
                <span className="font-medium text-red-800">Spam Detection</span>
              </div>
              <p className="text-sm text-red-700 mb-2">
                Spam Score: {lead.spamScore}/100
              </p>
              {lead.spamReasons && lead.spamReasons.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-red-800 mb-1">Reasons:</p>
                  <ul className="text-sm text-red-700 list-disc list-inside">
                    {lead.spamReasons.map((reason, index) => (
                      <li key={index}>{reason}</li>
                    ))}
                  </ul>
                </div>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleMarkAsNotSpam(lead._id)}
                className="mt-2 text-green-600 border-green-200 hover:bg-green-50"
              >
                Mark as Not Spam
              </Button>
            </div>
          )}

          <div className="text-xs text-muted-foreground">
            <p>Created: {formatDate(lead.createdAt)}</p>
            {lead.lastContactDate && (
              <p>Last Contact: {formatDate(lead.lastContactDate)}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  if (!user?.role?.permissions?.includes('view_leads') && !user?.role?.permissions?.includes('manage_leads')) {
    return (
      <div className="p-6">
        <div className="text-center">
          <h2 className="text-xl font-bold text-red-600 mb-2">Access Denied</h2>
          <p>You don't have permission to view leads.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Lead Management</h1>
          <p className="text-muted-foreground">Manage customer leads from your projects</p>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <Select 
                value={selectedProject || "all"} 
                onValueChange={(value) => setSelectedProject(value === "all" ? null : value as Id<"projects">)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select Project" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Projects</SelectItem>
                  {projects.map((project) => (
                    <SelectItem key={project._id} value={project._id}>
                      {project.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex-1 min-w-[200px]">
              <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                <SelectTrigger>
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="new">New</SelectItem>
                  <SelectItem value="contacted">Contacted</SelectItem>
                  <SelectItem value="qualified">Qualified</SelectItem>
                  <SelectItem value="converted">Converted</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex-1 min-w-[200px]">
              <Select value={selectedFormType} onValueChange={setSelectedFormType}>
                <SelectTrigger>
                  <SelectValue placeholder="All Form Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Form Types</SelectItem>
                  <SelectItem value="lead_form">Lead Form</SelectItem>
                  <SelectItem value="download_form">Download Form</SelectItem>
                  <SelectItem value="unknown">Unknown</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                <Input
                  placeholder="Search leads..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="includeSpam"
                checked={includeSpam}
                onChange={(e) => setIncludeSpam(e.target.checked)}
                className="rounded"
              />
              <label htmlFor="includeSpam" className="text-sm">Include Spam</label>
            </div>
          </div>
        </CardContent>
      </Card>

      {selectedProject !== null && (
        <>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList>
              <TabsTrigger value="all-leads">All Leads</TabsTrigger>
              <TabsTrigger value="download-leads">Download Form Leads</TabsTrigger>
              <TabsTrigger value="lead-form-leads">Lead Form Leads</TabsTrigger>
              <TabsTrigger value="spam-monitoring">Spam Monitoring</TabsTrigger>
            </TabsList>

            <TabsContent value="all-leads" className="space-y-6">
              <StatsCards title="All Leads Statistics" stats={leadStats} />
              <LeadTable leads={allLeads} title="All Leads" showPurpose={true} />
            </TabsContent>

            <TabsContent value="download-leads" className="space-y-6">
              <StatsCards title="Download Form Statistics" stats={leadStats} />
              <LeadTable leads={downloadLeads} title="Download Form Leads" />
            </TabsContent>

            <TabsContent value="lead-form-leads" className="space-y-6">
              <StatsCards title="Lead Form Statistics" stats={leadStats} />
              <LeadTable leads={leadFormLeads} title="Lead Form Leads" />
            </TabsContent>

            <TabsContent value="spam-monitoring" className="space-y-6">
              <StatsCards title="Spam Detection Statistics" stats={spamStats} />
              
              {spamStats && spamStats.recentSpamAttempts.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-red-600" />
                      Recent Spam Attempts
                    </CardTitle>
                    <CardDescription>
                      Latest submissions flagged as potential spam
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {spamStats.recentSpamAttempts.map((attempt) => (
                        <div key={attempt._id} className="border border-red-200 rounded-lg p-4 bg-red-50">
                          <div className="flex justify-between items-start">
                            <div>
                              <p className="font-medium">{attempt.name}</p>
                              <p className="text-sm text-muted-foreground">{attempt.email}</p>
                              <p className="text-sm text-red-700">
                                Spam Score: {attempt.spamScore}/100
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="text-xs text-muted-foreground">
                                {formatDate(attempt.createdAt)}
                              </p>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleMarkAsNotSpam(attempt._id)}
                                className="mt-2 text-green-600 border-green-200 hover:bg-green-50"
                              >
                                Not Spam
                              </Button>
                            </div>
                          </div>
                          {attempt.spamReasons.length > 0 && (
                            <div className="mt-2">
                              <p className="text-xs font-medium text-red-800">Reasons:</p>
                              <div className="flex flex-wrap gap-1 mt-1">
                                {attempt.spamReasons.map((reason, index) => (
                                  <Badge key={index} variant="destructive" className="text-xs">
                                    {reason}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </TabsContent>
          </Tabs>
        </>
      )}

      {selectedProject === null && (
        <Card>
          <CardContent className="pt-6 text-center">
            <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p className="text-muted-foreground">Please select a project to view leads</p>
          </CardContent>
        </Card>
      )}

      {/* Lead Detail Modal */}
      {selectedLead && (
        <LeadDetailModal
          lead={selectedLead}
          onClose={() => setSelectedLead(null)}
        />
      )}
    </div>
  );
};

export const LeadManagement: React.FC = () => {
  return (
    <Authenticated>
      <LeadManagementContent />
    </Authenticated>
  );
}; 